import { Head } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Select,
  Tabs,
  Text,
} from "@radix-ui/themes";
import { router } from "@inertiajs/react";
import { ArrowLeft, MapPin, Mail, CheckCircle2, Printer } from "lucide-react";
import {
  ManagerParticipantCheckInApplication,
  ManagerParticipantCheckInProps,
} from "@/types/manager_events";

const PARTICIPANT_TABS = [
  { key: "MediaGroup", label: "Media" },
  { key: "Staff",      label: "Staff" },
  { key: "Team",       label: "Teams" },
  { key: "Driver",     label: "Drivers" },
]

// Badges this track hands out. Hardcoded for now; revisit when other tracks
// need different identifiers (see the badge-checkout discussion).
const BADGES = Array.from({ length: 30 }, (_, i) => String(i + 1))

function statusBadgeColor(status: string): "yellow" | "green" | "red" | "gray" | "amber" {
  if (status === "approved") return "green"
  if (status === "pending")  return "yellow"
  if (status === "rejected") return "red"
  // The check-in query only loads pending and approved, so this is here to keep
  // the five statuses in one piece rather than because the gate will see it.
  if (status === "waitlisted") return "amber"
  return "gray"
}

function ApplicantRow({
  app,
  takenBadges,
}: {
  app: ManagerParticipantCheckInApplication
  takenBadges: Set<string>
}) {
  const handleCheckIn = () => {
    router.patch(app.check_in_path, {}, { preserveScroll: true })
  }

  const assignBadge = (badge: string | null) => {
    router.patch(app.assign_badge_path, { badge }, { preserveScroll: true })
  }

  return (
    <Box
      className="border-b py-4"
      style={{ borderColor: "var(--gray-6)" }}
    >
      <Flex
        direction={{ initial: "column", sm: "row" }}
        justify="between"
        align={{ initial: "start", sm: "center" }}
        gap="4"
      >
        <Flex direction="column" gap="1" style={{ flex: 1 }}>
          <Flex align="center" gap="2">
            <Text weight="medium">{app.participant.name}</Text>
            <Badge color={statusBadgeColor(app.status)} variant="soft" size="1">
              {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
            </Badge>
          </Flex>
          <Flex gap="3">
            {app.participant.location && (
              <Flex align="center" gap="1">
                <MapPin size={12} color="var(--gray-9)" />
                <Text size="1" color="gray">{app.participant.location}</Text>
              </Flex>
            )}
            {app.participant.email && (
              <Flex align="center" gap="1">
                <Mail size={12} color="var(--gray-9)" />
                <Text size="1" color="gray">{app.participant.email}</Text>
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex align="center" gap="2">
          <Button
            size="2"
            variant={app.attended ? "solid" : "soft"}
            color={app.attended ? "green" : "gray"}
            onClick={handleCheckIn}
          >
            <CheckCircle2 size={14} />
            {app.attended ? "Attended" : "Mark Attended"}
          </Button>
          <Select.Root
            value={app.badge ?? ""}
            onValueChange={(value) => assignBadge(value)}
          >
            <Select.Trigger placeholder="Badge" variant="soft" />
            <Select.Content position="popper">
              {BADGES.map((n) => (
                <Select.Item
                  key={n}
                  value={n}
                  disabled={takenBadges.has(n) && app.badge !== n}
                >
                  Badge {n}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {app.badge && (
            <Button
              size="2"
              variant="soft"
              color="gray"
              onClick={() => assignBadge(null)}
            >
              Return
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  )
}

export default function ParticipantCheckIn({ event, applications, paths }: ManagerParticipantCheckInProps) {
  const visibleTabs = PARTICIPANT_TABS.filter((tab) =>
    applications.some((a) => a.participant.participantable_type === tab.key)
  )

  const defaultTab = visibleTabs[0]?.key ?? ""

  // A physical badge can only be in one person's hands, so taken badges are
  // computed across every application for the event, not just the active tab.
  const takenBadges = new Set(
    applications.map((a) => a.badge).filter((b): b is string => b !== null)
  )

  return (
    <>
      <Head title="Participant Check In" />
      <Container size="3" py="6" px="4">
        <Flex direction="column" gap="5">
          {/* Header */}
          <Flex direction="column" gap="1">
            <Button
              asChild
              variant="ghost"
              color="gray"
              size="1"
              style={{ alignSelf: "flex-start" }}
            >
              <a href={paths.index}>
                <ArrowLeft size={14} />
                Back to Events
              </a>
            </Button>
            <Text size="6" weight="bold">{event.name}</Text>
            <Text size="2" color="gray">{event.date} · Participant Check In</Text>

            {/* Paper fallback: the gate's connection is unreliable and the front
                desk is busy. Print it, fill it in, photograph it, type it back in. */}
            <Button asChild variant="soft" size="2" mt="2" style={{ alignSelf: "flex-start" }}>
              <a href={paths.media_list}>
                <Printer size={14} />
                Print media list
              </a>
            </Button>
          </Flex>

          {/* Tabs */}
          {visibleTabs.length === 0 ? (
            <Text color="gray">No pending or approved applications for this event.</Text>
          ) : (
            <Tabs.Root defaultValue={defaultTab}>
              <Tabs.List>
                {visibleTabs.map((tab) => {
                  const tabApps = applications.filter(
                    (a) => a.participant.participantable_type === tab.key
                  )
                  const attendedCount = tabApps.filter((a) => a.attended).length
                  return (
                    <Tabs.Trigger key={tab.key} value={tab.key}>
                      {tab.label} ({attendedCount}/{tabApps.length})
                    </Tabs.Trigger>
                  )
                })}
              </Tabs.List>

              {visibleTabs.map((tab) => {
                const tabApps = applications.filter(
                  (a) => a.participant.participantable_type === tab.key
                )
                return (
                  <Tabs.Content key={tab.key} value={tab.key}>
                    <Box pt="3">
                      {tabApps.map((app) => (
                        <ApplicantRow
                          key={app.id}
                          app={app}
                          takenBadges={takenBadges}
                        />
                      ))}
                    </Box>
                  </Tabs.Content>
                )
              })}
            </Tabs.Root>
          )}
        </Flex>
      </Container>
    </>
  )
}
